import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Modal, Field } from "./AdminShared";

export default function CustomerModal({ data, onClose, onOpenOrder }) {
  const u0 = data.user;
  const [u, setU] = useState(u0);
  const [form, setForm] = useState({
    name: u0.name || "",
    email: u0.email || "",
    phone: u0.phone || "",
    notes: u0.notes || "",
    referral_credit: u0.referral_credit || 0,
    shipping_address: {
      line1: u0.shipping_address?.line1 || "",
      line2: u0.shipping_address?.line2 || "",
      city: u0.shipping_address?.city || "",
      state: u0.shipping_address?.state || "",
      postal_code: u0.shipping_address?.postal_code || u0.shipping_address?.zip || "",
      country: u0.shipping_address?.country || "",
    },
  });
  const [role, setRole] = useState(u0.role || "customer");
  const [saving, setSaving] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const ROLES = ["customer", "subscriber", "partner", "admin"];

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data: updated } = await http.put(`/admin/customers/${u.id}`, {
        name: form.name, email: form.email, phone: form.phone,
        notes: form.notes, referral_credit: Number(form.referral_credit) || 0,
        shipping_address: form.shipping_address,
      });
      setU(updated);
      toast.success("Customer updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const saveRole = async () => {
    setSavingRole(true);
    try {
      const { data: updated } = await http.patch(`/admin/customers/${u.id}/role`, { role });
      setU(updated);
      toast.success(`Role set to ${role}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Role change failed.");
    } finally { setSavingRole(false); }
  };

  const SA = form.shipping_address;
  return (
    <Modal title={u.name || u.email} onClose={onClose}>
      <div className="space-y-5 text-sm">
        {/* --- Editable profile --- */}
        <div className="border border-white/10 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">Profile</div>
            <button onClick={saveProfile} disabled={saving} className="btn-gold text-xs" data-testid="customer-save-btn">{saving ? "Saving…" : "Save Profile"}</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name"><input className="stc-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="customer-name-input"/></Field>
            <Field label="Email"><input className="stc-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="customer-email-input"/></Field>
            <Field label="Phone"><input className="stc-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="customer-phone-input"/></Field>
            <Field label={`Referral Credit ($)`}><input className="stc-input" type="number" step="0.01" min="0" value={form.referral_credit} onChange={e => setForm({...form, referral_credit: e.target.value})} data-testid="customer-credit-input"/></Field>
          </div>
          <Field label="Notes (internal — not shown to customer)">
            <textarea rows={2} className="stc-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="customer-notes-input"/>
          </Field>
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-2">Shipping Address</div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input className="stc-input" placeholder="Address line 1" value={SA.line1} onChange={e => setForm({...form, shipping_address: {...SA, line1: e.target.value}})} data-testid="customer-addr-line1"/>
              <input className="stc-input" placeholder="Address line 2" value={SA.line2} onChange={e => setForm({...form, shipping_address: {...SA, line2: e.target.value}})} data-testid="customer-addr-line2"/>
              <input className="stc-input" placeholder="City" value={SA.city} onChange={e => setForm({...form, shipping_address: {...SA, city: e.target.value}})} data-testid="customer-addr-city"/>
              <input className="stc-input" placeholder="State / Region" value={SA.state} onChange={e => setForm({...form, shipping_address: {...SA, state: e.target.value}})} data-testid="customer-addr-state"/>
              <input className="stc-input" placeholder="Postal code" value={SA.postal_code} onChange={e => setForm({...form, shipping_address: {...SA, postal_code: e.target.value}})} data-testid="customer-addr-postal"/>
              <input className="stc-input" placeholder="Country" value={SA.country} onChange={e => setForm({...form, shipping_address: {...SA, country: e.target.value}})} data-testid="customer-addr-country"/>
            </div>
          </div>
        </div>

        {/* --- Role assignment --- */}
        <div className="border border-white/10 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-1">Role</div>
              <div className="text-[#B5B0A7] text-xs">Current: <span className="text-[#F4F1EA] capitalize">{u.role}</span></div>
            </div>
            <div className="flex items-center gap-2">
              <select className="stc-input" value={role} onChange={e => setRole(e.target.value)} data-testid="customer-role-select">
                {ROLES.map(r => <option key={r} value={r}>{r[0].toUpperCase() + r.slice(1)}</option>)}
              </select>
              <button onClick={saveRole} disabled={savingRole || role === u.role} className="btn-outline text-xs border-[#C9A055] text-[#C9A055] disabled:opacity-40" data-testid="customer-role-save-btn">{savingRole ? "Saving…" : "Save Role"}</button>
            </div>
          </div>
        </div>

        {/* --- Read-only blocks --- */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Referral Code</div><div className="font-mono text-[#C9A055]">{u.referral_code}</div></div>
          <div><div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">Joined</div><div className="text-[#B5B0A7]">{new Date(u.created_at).toLocaleString()}</div></div>
        </div>

        <div>
          <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] mb-2">Orders ({data.orders.length})</div>
          <div className="border border-white/10 divide-y divide-white/5 max-h-[280px] overflow-y-auto">
            {data.orders.length === 0 ? <div className="p-3 text-[#8a857d] italic">No orders.</div> :
              data.orders.map(o => (
                <div key={o.id} onClick={() => onOpenOrder(o)} className="px-3 py-2.5 cursor-pointer hover:bg-white/5 flex justify-between items-center">
                  <div>
                    <div className="font-mono text-[#F4F1EA] text-xs">{o.order_number}</div>
                    <div className="text-[#B5B0A7] text-xs">{new Date(o.created_at).toLocaleDateString()} · {o.payment_method} · {(o.items || []).length} items</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#C9A055]">${(o.total || 0).toFixed(2)}</div>
                    <div className={`font-ui text-[9px] tracking-[0.22em] ${o.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{o.payment_status}</div>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
        {data.subscriptions.length > 0 && (
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] mb-2">Subscriptions</div>
            {data.subscriptions.map(s => (<div key={s.id} className="text-[#F4F1EA] text-sm">{s.status} · plan {s.plan_id}</div>))}
          </div>
        )}
        {data.referrals.length > 0 && (
          <div>
            <div className="font-ui text-[10px] tracking-[0.22em] text-[#C9A055] mb-2">Referrals made ({data.referrals.length})</div>
            {data.referrals.map(r => (<div key={r.id} className="text-[#B5B0A7] text-sm">{r.referred_email} — <span className={r.status === "qualified" ? "text-emerald-400" : "text-amber-400"}>{r.status}</span></div>))}
          </div>
        )}
      </div>
    </Modal>
  );
}
